import React, { FC, memo, useEffect, useState } from 'react'

import { useFocusNodeById } from '@please/lrud'
import type { Swiper as SwiperType } from 'swiper'
import SwiperCore, { Virtual, Keyboard } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/swiper-bundle.css'
import './styles.scss'

import { LineTile } from '@/components/Tiles/LineTile'
import {
  LineTileShape,
  LineTileSize,
  sliderMovementType,
} from '@/constants/common'
import { SlideDataType } from '@/types/sliderTypes'

import {
  createProgressiveGrid,
  createPushGrid,
  getActiveSlideIndex,
} from './SliderHelpers'

SwiperCore.use([Virtual, Keyboard])

type ExtendedSwiperType = SwiperType & {
  slidesSizesGrid: number[]
}
type Props = {
  swiper?: Nullable<SwiperType>
  sliderData: Array<SlideDataType>
  slidesOffsetBefore?: number
  slidesOffsetAfter?: number
  spaceBetween?: number
  initialSlide?: number
  speed?: number
  slidesPerView: number
  onSwiper: (swiper: SwiperType) => void
  setActiveSlideIndex: (activeSlideIndex: number) => void
  sliderId: string
  categoryId?: string
  tileShape?: LineTileShape
  tileSize?: LineTileSize
  movementType?: string
  setVirtual?: boolean
  broadcastId?: string
  onMouseOverTile?: (data: SlideDataType, focusId: string) => void
  initSlideId?: string
  activeSlideId?: string
}

const SliderRaw: FC<Props> = ({
  swiper,
  setActiveSlideIndex,
  sliderData,
  onSwiper,
  slidesPerView,
  speed = 200,
  slidesOffsetBefore = 24,
  slidesOffsetAfter = 0,
  spaceBetween = 0,
  initialSlide = 0,
  activeSlideId,
  sliderId,
  categoryId,
  tileShape = LineTileShape.ASPECT16x9,
  tileSize = LineTileSize.DEFAULT,
  movementType,
  setVirtual,
  broadcastId = '',
  onMouseOverTile,
  initSlideId,
}) => {
  const [currentTranslate, setCurrentTranslate] =
    useState<number>(slidesOffsetBefore)
  const [snapGridNext, setSnapGridNext] = useState<number[]>([])
  const [snapGridPrev, setSnapGridPrev] = useState<number[]>([])
  const [isInitTranslate, setIsInitTranslate] = useState(!!initSlideId)
  const focusNode = useFocusNodeById(sliderId)

  const isPushMovement = movementType === sliderMovementType.PUSH
  const isDefaultMovement = movementType === sliderMovementType.DEFAULT
  const isProgressiveMovement = movementType === sliderMovementType.PROGRESSIVE
  const isVirtualTranslate = isPushMovement || isProgressiveMovement

  useEffect(() => {
    sliderData && onSliderDataChange()
  }, [sliderData])

  useEffect(() => {
    if (!swiper) {
      return
    }
    focusNode?.isFocused ? swiper.keyboard.enable() : swiper.keyboard.disable()
  }, [swiper, focusNode?.isFocused])

  const getSlidesOffsetAfter = () => {
    if (isDefaultMovement && swiper) {
      const firstVisibleSlide = 1
      const restVisibleSlides = slidesPerView - firstVisibleSlide
      const [slideWidth] = (swiper as ExtendedSwiperType).slidesSizesGrid

      if (sliderData.length <= slidesPerView) {
        return slideWidth * Math.floor(restVisibleSlides)
      }
      return slideWidth * restVisibleSlides
    }

    return sliderData.length >= slidesPerView ? slidesOffsetAfter : 0
  }

  const onSwiperHandler = (swiper: SwiperType) => {
    onSwiper(swiper)
    if (isVirtualTranslate) {
      swiper.wrapperEl.style.transform = `translate3d(${slidesOffsetBefore}px, 0px, 0px)`
    }
  }

  const createSnapGrid = (swiper: SwiperType) => {
    if (isProgressiveMovement) {
      return createProgressiveGrid(
        swiper,
        slidesOffsetBefore,
        spaceBetween,
        slidesOffsetBefore,
        setSnapGridNext,
        setSnapGridPrev,
      )
    } else if (isPushMovement) {
      return createPushGrid(
        swiper,
        slidesOffsetBefore,
        slidesOffsetAfter,
        setSnapGridNext,
        setSnapGridPrev,
      )
    }
  }

  const customTranslate = (swiper: SwiperType) => {
    if (isPushMovement) {
      pushTranslate(swiper)
    } else if (isProgressiveMovement) {
      progressiveTranslate(swiper)
    }
  }

  const pushTranslate = (swiper: SwiperType) => {
    const activeIndex = swiper.activeIndex
    const previousIndex = swiper.previousIndex
    const isNextDirection = activeIndex > previousIndex
    const diffSlide = Math.abs(previousIndex - activeIndex)
    let snapGrid
    if (diffSlide > 1) {
      snapGrid =
        isNextDirection && !isInitTranslate ? snapGridNext : snapGridPrev
    } else {
      snapGrid = isNextDirection ? snapGridNext : snapGridPrev
    }
    if (diffSlide > 1 || currentTranslate === snapGrid[previousIndex]) {
      const translateValue = snapGrid[activeIndex]
      swiper.wrapperEl.style.transform = `translate3d(${translateValue}px, 0px, 0px)`
      setCurrentTranslate(translateValue)
    }

    setIsInitTranslate(false)
  }

  const progressiveTranslate = (swiper: any) => {
    const activeIndex = swiper.activeIndex
    const translateValue = snapGridNext[activeIndex]
    swiper.wrapperEl.style.transform = `translate3d(${translateValue}px, 0px, 0px)`
    setCurrentTranslate(translateValue)
  }

  const onSliderDataChange = () => {
    if (!swiper) {
      return
    }
    const activeIndex = getActiveSlideIndex(sliderData, activeSlideId)
    swiper.slideTo(activeIndex)
    if (isVirtualTranslate) {
      const snapGrid =
        snapGridPrev.length !== sliderData.length
          ? createSnapGrid(swiper)?.snapGridPrev
          : snapGridPrev

      const translateX = snapGrid[activeIndex]
      swiper.wrapperEl.style.transform = `translate3d(${translateX}px, 0px, 0px)`
      setCurrentTranslate(translateX)
    }
    setActiveSlideIndex(activeIndex)
  }

  return sliderData?.length ? (
    <Swiper
      tag="section"
      wrapperTag="ul"
      speed={speed}
      id={sliderId}
      centeredSlides={isVirtualTranslate}
      spaceBetween={spaceBetween}
      slidesPerView={slidesPerView}
      onSwiper={onSwiperHandler}
      slidesOffsetBefore={isVirtualTranslate ? 0 : slidesOffsetBefore}
      slidesOffsetAfter={isVirtualTranslate ? 0 : getSlidesOffsetAfter()}
      initialSlide={initialSlide}
      virtualTranslate={isVirtualTranslate}
      onSlideChange={isVirtualTranslate ? customTranslate : () => {}}
      onSnapGridLengthChange={(swiper: SwiperType) => {
        createSnapGrid(swiper)
      }}
      virtual={
        setVirtual && {
          cache: false,
          addSlidesBefore: Math.floor(+slidesPerView / 2),
          addSlidesAfter: Math.ceil(+slidesPerView / 2),
        }
      }
    >
      {sliderData.map((slide, i) => {
        return (
          <SwiperSlide
            key={`${sliderId}_${slide.id}_${i}`}
            id={`${sliderId}_${slide.id}_${i}`}
            tag="li"
            className="swiper-slide"
            virtualIndex={i}
          >
            <LineTile
              index={i}
              sliderId={sliderId}
              categoryId={categoryId}
              slide={slide}
              tileShape={tileShape}
              tileSize={tileSize}
              broadcastId={broadcastId}
              onMouseOverTile={onMouseOverTile}
              isActive={focusNode?.isFocused && initialSlide === i}
            />
          </SwiperSlide>
        )
      })}
    </Swiper>
  ) : null
}

export const Slider = memo(SliderRaw)
